import { Injectable } from '@angular/core';

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;
const PBKDF2_ITERATIONS = 100_000;
const KEY_CACHE_MAX = 16;

class CryptoLib {
    private static readonly encoder = new TextEncoder();
    private static readonly decoder = new TextDecoder();
    private readonly password: string;
    private readonly keyCache = new Map<string, CryptoKey>();

    constructor(password: string) {
        this.password = password;
    }

    private static encodeText(text: string): Uint8Array {
        return CryptoLib.encoder.encode(text);
    }

    private static arrayBufferToBase64(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    private static base64ToUint8Array(base64: string): Uint8Array {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    }

    private async deriveKey(salt: Uint8Array): Promise<CryptoKey> {
        const cacheKey = CryptoLib.arrayBufferToBase64(salt.buffer);

        const cached = this.keyCache.get(cacheKey);
        if (cached) return cached;

        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            CryptoLib.encodeText(this.password),
            { name: 'PBKDF2' },
            false,
            ['deriveKey']
        );

        const key = await crypto.subtle.deriveKey(
            { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
            keyMaterial,
            { name: ALGORITHM, length: KEY_LENGTH },
            false,
            ['encrypt', 'decrypt']
        );

        if (this.keyCache.size >= KEY_CACHE_MAX) {
            this.keyCache.delete(this.keyCache.keys().next().value);
        }
        this.keyCache.set(cacheKey, key);

        return key;
    }

    async encrypt(text: string): Promise<string> {
        const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
        const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
        const key = await this.deriveKey(salt);

        const encrypted = await crypto.subtle.encrypt(
            { name: ALGORITHM, iv },
            key,
            CryptoLib.encodeText(text)
        );

        const combined = new Uint8Array(SALT_LENGTH + IV_LENGTH + encrypted.byteLength);
        combined.set(salt, 0);
        combined.set(iv, SALT_LENGTH);
        combined.set(new Uint8Array(encrypted), SALT_LENGTH + IV_LENGTH);

        return CryptoLib.arrayBufferToBase64(combined.buffer);
    }

    async decrypt(encryptedData: string): Promise<string> {
        const data = CryptoLib.base64ToUint8Array(encryptedData);
        const salt = data.slice(0, SALT_LENGTH);
        const iv = data.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
        const ciphertext = data.slice(SALT_LENGTH + IV_LENGTH);

        const key = await this.deriveKey(salt);

        const decrypted = await crypto.subtle.decrypt(
            { name: ALGORITHM, iv },
            key,
            ciphertext
        );

        return CryptoLib.decoder.decode(decrypted);
    }
}

@Injectable({ providedIn: 'root' })
export class CryptoService {
    private lib: CryptoLib | null = null;

    Init(password: string): void {
        this.lib = new CryptoLib(password);
    }

    async encryptData(text: string): Promise<string | null> {
        if (!text) return null;
        if (!this.lib) throw new Error('[CryptoService] Not initialized — call Init() first');
        try {
            return await this.lib.encrypt(text);
        } catch (error) {
            console.error('[Crypto] Encryption failed:', error);
            return null;
        }
    }

    async decryptData(encryptedText: string): Promise<string | null> {
        if (!encryptedText) return null;
        if (!this.lib) throw new Error('[CryptoService] Not initialized — call Init() first');
        try {
            return await this.lib.decrypt(encryptedText);
        } catch (error) {
            console.error('[Crypto] Decryption failed:', error);
            return null;
        }
    }
}
